/**
 * @fileoverview Current Organisation Store — the DATABASE id (and name) of the organisation the user is in
 *
 * `OrgSwitcher` decides it once `GET /api/user/organisations` returns; Settings → My Organisation
 * links to it (#2200). It is held here, reactively, because that decision routinely lands AFTER
 * Settings has rendered: read straight from `localStorage`, the card stayed hidden until a reload
 * when Settings was the first page opened after sign-in.
 *
 * The NAME rides alongside the id for the same reason (Bug #2455): one login now spans several
 * organisations, so a destructive confirm must name the organisation it acts on, and the page
 * showing it has no other source for that name. It is set at the same moments as the id, from the
 * same `UserOrganisation`, so the two never describe different organisations.
 *
 * A seatless member is refused `GET /api/user/organisations`, so `OrgSwitcher` never fills it for them.
 * The account-access watch then sets the store from the `organisationId` on the 403
 * `SEAT_REQUIRED` body when the store is empty or holds a different id (id only, no name: a stale
 * name is cleared), so the Seats page can address its own organisation (#1842).
 *
 * Not the Auth0 organization id (`org_…`) — that is read from the token,
 * and it would 404 against `/admin/organisations/:id`.
 *
 * @module stores/current-organisation.store
 */

import { defineStore } from 'pinia'
import { ref } from 'vue'

/** Persisted so a reload keeps the user's choice between organisations. */
const STORAGE_KEY = 'sc_current_org_id'
/** Persisted beside the id so a reload keeps the name too. */
const NAME_STORAGE_KEY = 'sc_current_org_name'

export const useCurrentOrganisationStore = defineStore('current-organisation', () => {
  const id = ref<string | null>(localStorage.getItem(STORAGE_KEY))
  const name = ref<string | null>(localStorage.getItem(NAME_STORAGE_KEY))

  /** Records the organisation. `orgName` is stored with the id; omitting it clears any previous
   *  name rather than leaving the PREVIOUS organisation's name against the new id. */
  function set(orgId: string, orgName?: string | null): void {
    id.value = orgId
    localStorage.setItem(STORAGE_KEY, orgId)
    if (orgName) {
      name.value = orgName
      localStorage.setItem(NAME_STORAGE_KEY, orgName)
    } else {
      name.value = null
      localStorage.removeItem(NAME_STORAGE_KEY)
    }
  }

  function clear(): void {
    id.value = null
    name.value = null
    localStorage.removeItem(STORAGE_KEY)
    localStorage.removeItem(NAME_STORAGE_KEY)
  }

  return { id, name, set, clear }
})
